import { useCallback } from "react";
import { useSnapshot } from "valtio/react";
import { proxy } from "valtio/vanilla";
import type { AvailableLayerCombinations } from "../layers/types";
import { useLayers } from "../layers/useLayers";
import { ALL_SETTINGS_FOR_ALL_LAYER_COMBINATIONS } from "./layerCombinationSettings";
import type {
    AllSettingsForAllLayerCombinations,
    LayerCombinationSettings,
} from "./types";

type UpdateSettingsFn = (currentSettings: LayerCombinationSettings) => void;

const layerSettingsState = proxy(ALL_SETTINGS_FOR_ALL_LAYER_COMBINATIONS);

export function useLayerCombinationSettings() {
    const { isLayerCombinationShown } = useLayers();

    const settingsForShownLayerCombination =
        getSettingsForShownLayerCombination(
            layerSettingsState,
            isLayerCombinationShown,
        );

    const settingsSnapshot = useSnapshot(
        settingsForShownLayerCombination.settings,
    );

    const settingsUpdaterFn = useCallback(
        (updateSettingsFn: UpdateSettingsFn) => {
            const settingsForShownLayerCombination =
                getSettingsForShownLayerCombination(
                    ALL_SETTINGS_FOR_ALL_LAYER_COMBINATIONS,
                    isLayerCombinationShown,
                );

            updateSettingsFn(settingsForShownLayerCombination.settings);
        },
        [isLayerCombinationShown],
    );

    return {
        layerCombinationSettings: settingsSnapshot,
        updateLayerCombinationSettings: settingsUpdaterFn,
    };
}

function getSettingsForShownLayerCombination(
    allSettingsForAllLayerCombinations: AllSettingsForAllLayerCombinations,
    isLayerCombinationShown: (
        ...layerCombination: AvailableLayerCombinations
    ) => boolean,
) {
    const settingsForShownLayerCombination =
        allSettingsForAllLayerCombinations.find((settings) =>
            isLayerCombinationShown(...settings.layerCombination),
        );

    if (settingsForShownLayerCombination === undefined) {
        throw new Error(
            "Settings for currently shown layer combination not found. This should normally not happen.",
        );
    }

    return settingsForShownLayerCombination;
}
